import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FileTypes } from '@guryou/core';
import {
  DefaultText,
  HtmlAudioPlayer,
  HtmlVideoPlayer,
  IconButton,
  imageNotFound,
  ImgIcon,
  LeftIcon,
  MediumText,
  PdfIcon,
  PlayIcon,
  RightIcon,
  Spinner,
} from '@guryou/html-components';
import { getCustomerPublicFile, stateIsLoading, useEffectOnSuccess } from '@guryou/redux';

const getPlayerByType = (type, src, handleError, cantPreviewFile) => {
  if (type === FileTypes.video) {
    return <HtmlVideoPlayer src={src} />;
  }
  if (type === FileTypes.audio) {
    return <HtmlAudioPlayer src={src} />;
  }
  if (type === FileTypes.image) {
    return <img src={src} style={{ maxHeight: '500px' }} alt="section img" onError={handleError} />;
  }
  if (type === FileTypes.document) {
    return <DefaultText color="#111827">{cantPreviewFile}</DefaultText>;
  }
  return <DefaultText color="#111827">Something went wrong!</DefaultText>;
};

const MyCustomPreviewModal = ({ items, active, providerId, handleFileSeenUnseen, allowPreview }) => {
  const [currentActive, setCurrentActive] = useState(active);
  const [currentItems, setCurrentItems] = useState(() => {
    if (allowPreview) {
      return (items || []).map(x => (x.id === active ? { ...x, seen: true } : x));
    }

    return (items || []).filter(x => x.free);
  });

  const { t } = useTranslation();

  const file = useSelector(state => state.files.getPublic);

  const dispatch = useDispatch();

  const [data, setData] = useState({
    type: null,
    src: null,
  });

  const handleFileSeen = useCallback(
    id => {
      handleFileSeenUnseen(id, true);
      setCurrentItems(prev => prev.map(x => (x.id === id ? { ...x, seen: true } : x)));
    },
    [handleFileSeenUnseen]
  );

  const handleError = () => {
    setData({ type: FileTypes.image, src: imageNotFound }); // Set to default image if the original fails
  };

  useEffectOnSuccess(
    state => state.files.getPublic,
    data => {
      setData({ type: data.type, src: data?.signedUrl });
    }
  );

  const handleClick = id => {
    setCurrentActive(id);
  };

  const handleShift = item => {
    if (allowPreview || item.free) {
      setCurrentActive(item.id);
    }
  };

  const handleNext = () => {
    const nextItem = currentItems[currentIndex + 1];
    handleShift(nextItem);
  };

  const handlePrev = () => {
    const prevItem = currentItems[currentIndex - 1];
    handleShift(prevItem);
  };

  const currentItem = currentItems.find(x => x.id === currentActive);
  const currentIndex = currentItems.findIndex(x => x.id === currentActive);

  useEffect(() => {
    if (!currentActive || !currentItems?.length) return;
    const activeItem = currentItems.find(item => item.id === currentActive);
    if (!activeItem) return;

    if (allowPreview && !activeItem.seen) {
      handleFileSeen(activeItem.id);
    }

    if (activeItem.type === FileTypes.document) {
      setData({ type: FileTypes.document, src: '123' });
      return;
    }

    dispatch(getCustomerPublicFile(activeItem.id, +providerId));
  }, [currentActive, currentItems, providerId, allowPreview, dispatch, handleFileSeen]);

  if (!data.type || !data.src) {
    return (
      <div className="flex h-[500px] w-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full w-full">
      <div className="grid h-full grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-3">
        <div className="h-full rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200 bg-white">
            <h3 className="text-sm font-semibold text-gray-900">{t('VIDEO_COURSE_PAGE.courseContent')}</h3>
          </div>
          <div className="h-[calc(100%-52px)] overflow-y-auto p-3 bg-[#f7f8fa]">
            {currentItems.map(x => (
              <button
                key={x.id}
                type="button"
                onClick={() => handleClick(x.id)}
                className={`w-full text-left mb-2 rounded-xl border px-2.5 py-2.5 transition-colors ${
                  x.id === currentActive
                    ? 'bg-[color:color-mix(in_srgb,var(--feel-primary)_10%,white)] border-[color:color-mix(in_srgb,var(--feel-primary)_30%,#eceff4)]'
                    : x.seen
                    ? 'bg-[color:color-mix(in_srgb,var(--feel-primary)_10%,white)] border-[color:color-mix(in_srgb,var(--feel-primary)_30%,#eceff4)]'
                    : 'bg-white border-[#eceff4] hover:bg-[#f3f5f8] hover:border-[#e1e5eb]'
                }`}
                style={x.id === currentActive ? { boxShadow: 'inset 3px 0 0 var(--feel-primary)' } : undefined}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className="w-8 h-8 rounded-lg border flex items-center justify-center"
                      style={{
                        color: 'var(--feel-primary)',
                        borderColor: 'color-mix(in srgb, var(--feel-primary) 25%, #eceff4)',
                        backgroundColor: 'color-mix(in srgb, var(--feel-primary) 10%, white)',
                      }}
                    >
                      {x.type === FileTypes.document ? <PdfIcon /> : x.type === FileTypes.image ? <ImgIcon /> : <PlayIcon />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] leading-5 font-semibold text-gray-800 truncate">{x.title || x.name}</p>
                      <p className="text-[11px] text-gray-500">
                        {(() => {
                          const typeLabel =
                            x.type === FileTypes.video
                              ? t('VIDEO_COURSE_PAGE.video')
                              : x.type === FileTypes.document
                              ? t('VIDEO_COURSE_PAGE.document')
                              : t('VIDEO_COURSE_PAGE.image');
                          return x.duration ? `${x.duration} • ${typeLabel}` : typeLabel;
                        })()}
                      </p>
                    </div>
                  </div>
                  {!allowPreview && <span className="rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-500">Lock</span>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="h-full rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
            <MediumText xs={10} color="#111827" padding={0} margin={0}>
              {currentItem.title || currentItem.name}
            </MediumText>
            <div className="flex items-center gap-1">
              <IconButton color={currentIndex < 1 ? 'gray' : 'lightgray'} disabled={currentIndex < 1} onClick={() => handlePrev()}>
                <LeftIcon />
              </IconButton>
              <IconButton
                color={currentIndex + 1 >= currentItems.length ? 'gray' : 'lightgray'}
                disabled={currentIndex + 1 >= currentItems.length}
                onClick={() => handleNext()}
              >
                <RightIcon />
              </IconButton>
            </div>
          </div>
          <div className="h-[calc(100%-58px)] flex items-center justify-center p-4">
            {stateIsLoading(file) ? <Spinner /> : getPlayerByType(data.type, data.src, handleError, t('VIDEO_COURSE_PAGE.cantPreviewFile'))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyCustomPreviewModal;
